import { useEffect, useRef, useState } from "react";
import {
  getWindowDetails,
  closeIfSecondaryWindow,
} from "../Helpers/navigationHelper";
import { useGenericComponentsStateService } from "../Context/GenericComponentsStateContext";
import { useCallsService } from "../Context/CallsContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import SpinnerComponent from "../Components/GenericComponents/SpinnerComponent";
import CallComponent from "../Components/AudioVideoCalls/CallComponent";
import { useMeetingsService } from "../Context/MeetingContext";
import { joinConferenceCallApiCall } from "../API/conferences/conferencesService";
import { useWebSocketDataService } from "../Context/WebsocketContext";
import WebSocketObserver from "../Helpers/websocket/WebSocketObserver";
import AccessRequestSentPage from "../Components/Meetings/AccessRequestSentPage";
import { useNotify } from "../Helpers/Notifications";
import { useTranslate } from "@tolgee/react";
import { meetingJoinErrorMessage } from "../Helpers/meetingJoinErrorHelper";
import useRetryWhenMeetingOpens from "../hooks/useRetryWhenMeetingOpens";
import moment from "moment";
import {
  ERROR_MEETING_NOT_OPEN,
  ERROR_MEETING_PRODUCT_UNAVAILABLE,
  ERROR_MEETING_WAITING_FOR_HOST,
  MEETING_LIFECYCLE_STARTED,
} from "../Constants/constantValues";

const CallWrapperPage = () => {
  // Check if we're in a secondary window
  const { setLoading } = useGenericComponentsStateService();
  const {
    selectedCall,
    getCallDetails,
    activeCallRef,
    setSelectedCall,
    setSelectedCallAccess,
  } = useCallsService();

  const { setCurrentMeeting, currentMeetingRef, getMeetingById } =
    useMeetingsService();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslate();
  const { notifyError } = useNotify();
  const { add_observer, remove_observer } = useWebSocketDataService();
  const [waitingForHost, setWaitingForHost] = useState(false);
  const [opensAt, setOpensAt] = useRetryWhenMeetingOpens(() => fetchCallUUID(searchParams.get("uuid")));

  const handleMeetingStartedRef = useRef(null);
  const meetingObserverRef = useRef(
    new WebSocketObserver("MeetingChannel", (message) => handleMeetingStartedRef.current?.(message))
  );

  handleMeetingStartedRef.current = (message) => {
    const response = typeof message === "string" ? JSON.parse(message) : message;
    const meetingId = searchParams.get("meeting_id");
    if (!waitingForHost || response.status !== MEETING_LIFECYCLE_STARTED || String(response.meeting_id) !== meetingId) return;

    remove_observer(meetingObserverRef.current);
    setWaitingForHost(false);
    fetchCallUUID(searchParams.get("uuid"));
  };

  const fetchCallUUID = (uuid) => {
    setLoading(true);
    getCallDetails(
      uuid,
      (response) => {
        activeCallRef.current = response.data;
        setSelectedCall(response.data.details);
        setSelectedCallAccess(response.data.access);
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        const errorCode = error?.response?.data?.error_code;
        if (errorCode === ERROR_MEETING_NOT_OPEN) {
          setOpensAt(error.response.data.opens_at);
          return;
        }
        if (errorCode === ERROR_MEETING_WAITING_FOR_HOST) {
          remove_observer(meetingObserverRef.current);
          add_observer(meetingObserverRef.current);
          setWaitingForHost(true);
          return;
        }
        if (errorCode === ERROR_MEETING_PRODUCT_UNAVAILABLE) {
          notifyError(meetingJoinErrorMessage(error, t));
        }
        console.log(error);
        exit();
      }
    );
  };

  const fetchConferenceCall = (conferenceId) => {
    setLoading(true);
    joinConferenceCallApiCall(
      conferenceId,
      (response) => {
        const callDetails =
          response?.data?.details ||
          response?.data?.call ||
          response?.data?.conference_call;
        if (!callDetails) {
          setLoading(false);
          exit();
          return;
        }
        const withConferenceFlag = { ...callDetails, conference_id: conferenceId };
        activeCallRef.current = {
          details: withConferenceFlag,
          access: response?.data?.access,
        };
        setSelectedCall(withConferenceFlag);
        setSelectedCallAccess(response?.data?.access);
        setLoading(false);
      },
      () => {
        setLoading(false);
        exit();
      }
    );
  };

  const handleGetMeetingById = (meetingId) => {
    getMeetingById(
      meetingId,
      (response) => {
        const meeting = response.data.meeting;
        currentMeetingRef.current = meeting;
        setCurrentMeeting(meeting);
      },
      (error) => { }
    );
  };

  const exit = () => {
    navigate("/", {
      replace: true
    });
  };

  useEffect(() => {
    let uuid = searchParams.get("uuid");
    if (!uuid) {
      exit();
      return;
    }

    const conferenceId = searchParams.get("conference_id");
    if (conferenceId && conferenceId !== "null") {
      fetchConferenceCall(conferenceId);
    } else {
      fetchCallUUID(uuid);
      let meetingId = searchParams.get("meeting_id");
      if (meetingId && meetingId !== "null") {
        handleGetMeetingById(meetingId);
      }
    }
  }, [searchParams.toString()]);

  const handleClose = async () => {
    closeIfSecondaryWindow();
  };

  useEffect(() => () => remove_observer(meetingObserverRef.current), []);

  return (
    <Container>
      {opensAt ? (
        <AccessRequestSentPage
          title={t("meeting_onboard.meeting_not_open_title")}
          subtitle={t("meeting_onboard.meeting_not_open_subtitle", { time: moment(opensAt).format("LLL") })}
          closeMeeting={exit}
        />
      ) : waitingForHost ? (
        <AccessRequestSentPage
          title={t("meeting_onboard.waiting_for_host_title")}
          subtitle={t("meeting_onboard.waiting_for_host_subtitle")}
          closeMeeting={exit}
        />
      ) : (
        <SpinnerComponent isLoading={!selectedCall} />
      )}
      {selectedCall && <CallComponent />}
    </Container>
  );
};

export default CallWrapperPage;

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  background: ${({ theme }) => theme.menu.background};
`;
